// lib/services/compute/sticker_cpu_slot.ts

import BaseSlot, { SlotState } from "../lib/services/compute/base_slot";

/**
 * Per-frame readback slot for sticker detection results.
 *
 * Holds a mappable GPU buffer that receives a copy of the sticker buffer
 * from a ColorGpuSlot, plus a CPU-side typed array view of the mapped
 * data.
 *
 * Lifecycle:
 *   Idle -> Recording (copy command encoded) -> Pending (mapAsync in
 *   flight) -> Ready (data available on CPU) -> Leased (consumer
 *   reading) -> Idle.
 */
export default class StickerCpuSlot extends BaseSlot {
  sticker_readback_buffer: GPUBuffer | null = null;
  blob_readback_buffer: GPUBuffer | null = null;

  sticker_buffer_size: number = 0;
  blob_buffer_size: number = 0;

  mapped_sticker_data: Uint8Array | null = null;
  mapped_blob_data: Uint8Array | null = null;


  // Which gpu slot sequence this readback corresponds to.
  source_gpu_sequence: number = 0;

  constructor() {
    super(false);
  }

  allocate_buffer(device: GPUDevice, key: string, slot_index: number, sticker_size: number,blob_size: number): void {
    this.sticker_buffer_size = sticker_size;
    this.blob_buffer_size = blob_size;

    this.sticker_readback_buffer = device.createBuffer({
    label: `${key}_sticker_readback_${slot_index}`,
    size: sticker_size,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });

  this.blob_readback_buffer = device.createBuffer({
    label: `${key}_blob_readback_${slot_index}`,
    size: blob_size,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });
    
  }

  begin_record(): boolean {
    if (this.state !== SlotState.Idle) return false;
    if (this.mapped_sticker_data) return false;
    if (this.mapped_blob_data) return false;

    if (this.sticker_readback_buffer?.mapState &&
    this.sticker_readback_buffer.mapState !== "unmapped") return false;

    if (this.blob_readback_buffer?.mapState &&
      this.blob_readback_buffer.mapState !== "unmapped") return false;

    this.transition_to(SlotState.Recording);
    return true;
  }

  /**
   * Encode the copy from the gpu slot's sticker buffer into this
   * readback buffer. Called during command encoding, before submit.
   */
  encode_copy(
    encoder: GPUCommandEncoder,
    sticker_source_buffer: GPUBuffer,
    blob_source_buffer: GPUBuffer,
    gpu_sequence: number
  ): void {

     /* console.log("[encode_copy]", {
      gpu_sequence,
      sticker_source_label: sticker_source_buffer.label,
      blob_source_label: blob_source_buffer.label,
      sticker_readback_label: this.sticker_readback_buffer?.label,
      blob_readback_label: this.blob_readback_buffer?.label,
      blob_buffer_size: this.blob_buffer_size,
      });*/

      encoder.copyBufferToBuffer(
      sticker_source_buffer, 0,
      this.sticker_readback_buffer!, 0,
      this.sticker_buffer_size
    );

    encoder.copyBufferToBuffer(
      blob_source_buffer, 0,
      this.blob_readback_buffer!, 0,
      this.blob_buffer_size
    );
    this.source_gpu_sequence = gpu_sequence;
    this.transition_to(SlotState.Pending);
  }

  /**
   * Start the async map operation. Called after the command buffer has
   * been submitted. Resolves when the data is available on CPU.
   */
  async begin_map(): Promise<boolean> {
  if (this.state !== SlotState.Pending) return false;

  try {
    await this.sticker_readback_buffer!.mapAsync(
      GPUMapMode.READ,
      0,
      this.sticker_buffer_size
    );
    const sticker_range = this.sticker_readback_buffer!.getMappedRange(
      0,
      this.sticker_buffer_size
    );
    this.mapped_sticker_data = new Uint8Array(sticker_range);

    await this.blob_readback_buffer!.mapAsync(
      GPUMapMode.READ,
      0,
      this.blob_buffer_size
    );
    const blob_range = this.blob_readback_buffer!.getMappedRange(
      0,
      this.blob_buffer_size
    );
    this.mapped_blob_data = new Uint8Array(blob_range);

  /*  console.log("[begin_map]", {
  source_gpu_sequence: this.source_gpu_sequence,
  blob_readback_label: this.blob_readback_buffer?.label,
  blob_buffer_size: this.blob_buffer_size,
  mapState: this.blob_readback_buffer?.mapState,
});*/

    this.transition_to(SlotState.Ready);
    return true;
  } catch (err) {
    console.error("StickerCpuSlot.begin_map failed", err);

    try { this.sticker_readback_buffer?.unmap(); } catch {}
    try { this.blob_readback_buffer?.unmap(); } catch {}

    this.mapped_sticker_data = null;
    this.mapped_blob_data = null;
    this.transition_to(SlotState.Idle);
    return false;
  }
}

  /**
   * Release the mapped buffer and return to Idle.
   */

  clear():void
  {
    //this.mapped_sticker_data?.fill(0);
    try { this.sticker_readback_buffer?.unmap(); } catch {}
    try { this.blob_readback_buffer?.unmap(); } catch {}

    this.mapped_sticker_data = null;
    this.mapped_blob_data = null;   

  }
  
  unmap_and_recycle(): void {
  if (this.sticker_readback_buffer?.mapState === "mapped") {
    this.sticker_readback_buffer.unmap();
  }
  if (this.blob_readback_buffer?.mapState === "mapped") {
    this.blob_readback_buffer.unmap();
  }

  this.mapped_sticker_data = null;
  this.mapped_blob_data = null;
  this.source_gpu_sequence = 0;
  this.recycle();
}

cancel_and_recycle(): void {
  try { this.sticker_readback_buffer?.unmap(); } catch {}
  try { this.blob_readback_buffer?.unmap(); } catch {}

  this.mapped_sticker_data = null;
  this.mapped_blob_data = null;
  this.source_gpu_sequence = 0;
  this.recycle();
}

recycle(): void {
  if (this.sticker_readback_buffer && this.mapped_sticker_data) {
    try { this.sticker_readback_buffer.unmap(); } catch {}
  }
  if (this.blob_readback_buffer && this.mapped_blob_data) {
    try { this.blob_readback_buffer.unmap(); } catch {}
  }

  this.mapped_sticker_data = null;
  this.mapped_blob_data = null;
  this.source_gpu_sequence = 0;
  super.recycle();
}

  destroy_buffers(): void {
    if (this.sticker_readback_buffer) {
      try { this.sticker_readback_buffer.unmap(); } catch {}
      this.sticker_readback_buffer.destroy();
      this.sticker_readback_buffer = null;
    }

    if (this.blob_readback_buffer) {
      try { this.blob_readback_buffer.unmap(); } catch {}
      this.blob_readback_buffer.destroy();
      this.blob_readback_buffer = null;
    }

    this.mapped_sticker_data = null;
    this.mapped_blob_data = null;
  }
    
}